import { useState, useRef, useCallback, useEffect } from "react";
import {
  subscribeToTopicWithMessages,
  sendMessage,
  loadOlderMessages as loadOlderTinodeMessages,
} from "../services/tinodeApi";
import { parseTinodeError } from "../utils/tinodeErrorHelper";
import {
  formatMessageForDashboard,
  sortMessagesBySeq,
  getComparableContent,
} from "../utils/tinodeMessageUtils";
import { iterateTopicMessages } from "../utils/tinodeTopicHelper";
import { isSessionEndMessage } from "../services/tinodeSessionService";

export const useTinodeMessages = (
  tinodeRef,
  selectedTopic,
  onMessageReceived = null,
) => {
  const [messages, setMessages] = useState([]);
  const [topicInfo, setTopicInfo] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageSeqSetRef = useRef(new Set());
  const optimisticMessagesRef = useRef(new Map());
  const pendingRealSeqRef = useRef(new Set());
  // FIX: Mapping optId → realSeq untuk tracking yang lebih akurat
  const optIdToRealSeqRef = useRef(new Map());
  // FIX: Mapping realSeq → optId untuk reverse lookup
  const realSeqToOptIdRef = useRef(new Map());
  const onMessageReceivedRef = useRef(onMessageReceived);
  onMessageReceivedRef.current = onMessageReceived;

  const handleIncomingMessage = useCallback((msg, topicName) => {
    if (!msg || !msg.seq) {
      return;
    }

    const realSeq = msg.seq;

    // FIX: Skip echo messages (Tinode mengirim 2 event: echo dengan seq tinggi dan real dengan seq normal)
    // Echo messages biasanya memiliki seq > 1000000 (sama seperti logic sebelumnya)
    const TINODE_ECHO_THRESHOLD = 1000000;
    if (typeof realSeq === "number" && realSeq > TINODE_ECHO_THRESHOLD) {
      // Ini adalah echo message, skip karena akan ada real message dengan seq normal
      return;
    }

    // FIX: Skip if already in Set (prevent duplicate) - sama seperti Chats.jsx
    if (messageSeqSetRef.current.has(realSeq)) {
      return;
    }

    // FIX: Add to Set FIRST to prevent race condition - sama seperti Chats.jsx
    messageSeqSetRef.current.add(realSeq);

    const formatted = formatMessageForDashboard(msg, topicName);

    if (onMessageReceivedRef.current) {
      onMessageReceivedRef.current(msg);
    }

    if (isSessionEndMessage(msg)) return;

    // FIX: Selalu gunakan Map untuk memastikan tidak ada duplikasi berdasarkan seq - sama seperti Chats.jsx
    setMessages((prev) => {
      // FIX: Pastikan tidak ada duplikasi berdasarkan seq - gunakan Map dengan seq sebagai key (sama seperti Chats.jsx)
      const existingMap = new Map(prev.map((m) => [m.seq, m]));

      // FIX: Hapus echo messages (seq tinggi) yang mungkin sudah masuk ke state
      // Tinode mengirim 2 event: echo (seq tinggi) dan real (seq normal)
      // Jika real message datang, hapus echo dengan content yang sama
      const TINODE_ECHO_THRESHOLD = 1000000;
      const msgContentNormalized = getComparableContent(msg);
      if (msgContentNormalized) {
        const echoSeqsToRemove = [];
        for (const [seq, msgInMap] of existingMap.entries()) {
          if (seq === realSeq) continue;
          // Cek apakah ini echo message (seq tinggi) dengan content yang sama
          if (typeof seq === "number" && seq > TINODE_ECHO_THRESHOLD) {
            const mapContent = getComparableContent(msgInMap);
            if (mapContent === msgContentNormalized) {
              echoSeqsToRemove.push(seq);
            }
          }
        }
        // Hapus echo messages
        echoSeqsToRemove.forEach((seq) => {
          existingMap.delete(seq);
          messageSeqSetRef.current.delete(seq);
        });
      }

      // FIX: Handle optimistic messages - hapus optimistic jika ada mapping ke realSeq (sama seperti Chats.jsx)
      // Chats.jsx tidak menggunakan optimistic messages, jadi kita hanya hapus berdasarkan mapping
      const optId = realSeqToOptIdRef.current.get(realSeq);
      if (optId) {
        // Ada mapping, hapus optimistic berdasarkan mapping
        if (existingMap.has(optId)) {
          existingMap.delete(optId);
          messageSeqSetRef.current.delete(optId);
        }
        // Hapus dari refs
        optimisticMessagesRef.current.delete(optId);
        optIdToRealSeqRef.current.delete(optId);
        realSeqToOptIdRef.current.delete(realSeq);
        pendingRealSeqRef.current.delete(realSeq);
      }

      // FIX: Jika sudah ada di map, update (sama seperti Chats.jsx)
      if (existingMap.has(realSeq)) {
        existingMap.set(realSeq, formatted);
        return sortMessagesBySeq(Array.from(existingMap.values()));
      }

      // FIX: Tambahkan pesan baru (sama seperti Chats.jsx)
      existingMap.set(realSeq, formatted);
      return sortMessagesBySeq(Array.from(existingMap.values()));
    });
  }, []);

  const loadTopicMessages = useCallback(
    async (topicName) => {
      if (!tinodeRef.current || !topicName) return null;

      try {
        let topic = tinodeRef.current.getTopic(topicName);

        if (!topic || !topic.isSubscribed()) {
          topic = await subscribeToTopicWithMessages(
            tinodeRef.current,
            topicName,
          );
        }

        if (!topic) return null;

        messageSeqSetRef.current.clear();
        pendingRealSeqRef.current.clear();
        optimisticMessagesRef.current.clear();
        optIdToRealSeqRef.current.clear();
        realSeqToOptIdRef.current.clear();

        topic.onData = (msg) => handleIncomingMessage(msg, topicName);

        const existing = [];
        const existingSeqSet = new Set(); // FIX: Gunakan Set lokal untuk de-duplikasi

        iterateTopicMessages(topic, (m) => {
          if (!m?.seq) return;
          if (isSessionEndMessage(m)) return;
          // FIX: Skip jika seq sudah ada di messageSeqSetRef atau di existingSeqSet (prevent duplicate)
          if (messageSeqSetRef.current.has(m.seq) || existingSeqSet.has(m.seq))
            return;
          messageSeqSetRef.current.add(m.seq);
          existingSeqSet.add(m.seq);
          existing.push(formatMessageForDashboard(m, topicName));
        });

        // FIX: De-duplikasi final sebelum set state
        const uniqueExisting = [];
        const seenSeqs = new Set();
        existing.forEach((msg) => {
          if (msg.seq && !seenSeqs.has(msg.seq)) {
            seenSeqs.add(msg.seq);
            uniqueExisting.push(msg);
          }
        });

        setMessages(
          uniqueExisting.length > 0 ? sortMessagesBySeq(uniqueExisting) : [],
        );

        setTopicInfo({
          name: topicName,
          seq: topic.seq || 0,
          public: topic.public || {},
          private: topic.private || {},
        });

        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);

        return topic;
      } catch (err) {
        // FIX: Handle 503 error - reconnect and retry
        const is503Error =
          err?.code === 503 ||
          err?.text === "Websocket is not connected" ||
          err?.message?.includes("Websocket is not connected") ||
          err?.message?.includes("503");

        if (is503Error && tinodeRef.current) {
          console.warn(
            "[useTinodeMessages] Websocket not connected (503), attempting to reconnect...",
          );

          try {
            const { connectTinodeInstance, attemptTinodeRelogin } =
              await import("../services/tinodeSingleton");

            if (!tinodeRef.current.isConnected()) {
              await connectTinodeInstance(tinodeRef.current);
            }

            if (!tinodeRef.current.isAuthenticated()) {
              await attemptTinodeRelogin(tinodeRef.current);
            }

            // Wait for connection to stabilize
            await new Promise((r) => setTimeout(r, 500));

            // Retry loadTopicMessages
            if (
              tinodeRef.current.isConnected() &&
              tinodeRef.current.isAuthenticated()
            ) {
              console.log(
                "[useTinodeMessages] Reconnected, retrying loadTopicMessages...",
              );
              return await loadTopicMessages(topicName);
            }
          } catch (reconnectErr) {
            console.error(
              "[useTinodeMessages] Reconnect failed:",
              reconnectErr,
            );
          }
        }

        if (process.env.NODE_ENV === "development") {
          console.warn(
            "[useTinodeMessages] loadTopicMessages failed:",
            parseTinodeError(err),
          );
        }
        throw err;
      }
    },
    [tinodeRef, handleIncomingMessage],
  );

  const sendOptimisticMessage = useCallback(
    async (text, topicName, options = {}) => {
      const { skipSend = false, imageData = null } = options;
      if (!text?.trim() || !topicName || !tinodeRef.current) return null;

      const content = text.trim();
      const timestamp = Date.now();
      const optId = `temp_${timestamp}_${Math.random().toString(36).substr(2, 5)}`;

      optimisticMessagesRef.current.set(optId, { content, timestamp });
      messageSeqSetRef.current.add(optId);

      const currentUserId = tinodeRef.current.getCurrentUserID?.() || "";

      setMessages((prev) => {
        // FIX: Gunakan Map untuk de-duplikasi berdasarkan seq
        const map = new Map();

        // Tambahkan semua pesan existing ke map (de-duplikasi)
        prev.forEach((m) => {
          if (m.seq && !map.has(m.seq)) {
            map.set(m.seq, m);
          }
        });

        // Tambahkan optimistic message hanya jika belum ada
        if (!map.has(optId)) {
          const draftyData = imageData
            ? {
                ent: [
                  {
                    tp: "IM",
                    data: {
                      bits: imageData.base64,
                      mime: imageData.mime,
                      width: imageData.width,
                      height: imageData.height,
                      name: imageData.name,
                      size: imageData.size,
                    },
                  },
                ],
                txt: content !== "📷 Image" ? content : "",
              }
            : null;
          map.set(optId, {
            seq: optId,
            from: currentUserId,
            content,
            data: draftyData,
            ts: new Date(timestamp).toISOString(),
            timestamp,
            topic: topicName,
            isOptimistic: true,
          });
        }

        return sortMessagesBySeq(Array.from(map.values()));
      });

      try {
        if (skipSend) {
          return optId;
        }
        const result = await sendMessage(tinodeRef.current, topicName, content);

        // Coba berbagai cara untuk mendapatkan seq
        const realSeq =
          result?.params?.seq ||
          result?.seq ||
          result?.ctrl?.params?.seq ||
          null;

        if (realSeq) {
          // FIX: Simpan mapping optId → realSeq untuk tracking yang lebih akurat
          optIdToRealSeqRef.current.set(optId, realSeq);
          realSeqToOptIdRef.current.set(realSeq, optId);

          // FIX: Tambahkan ke pendingRealSeqRef untuk tracking
          pendingRealSeqRef.current.add(realSeq);

          // FIX: JANGAN update pesan optimistic dengan realSeq di sini
          // Biarkan handleIncomingMessage yang menghapus optimistic dan menambahkan real message
          // Ini memastikan pesan real benar-benar mengganti optimistic (sama seperti Chats.jsx)
          // optimisticMessagesRef tetap menyimpan optId sampai pesan real datang di handleIncomingMessage
        } else {
          messageSeqSetRef.current.delete(optId);
          optimisticMessagesRef.current.delete(optId);
        }

        return result;
      } catch (err) {
        console.error("Failed to send message:", err);
        setMessages((prev) => prev.filter((m) => m.seq !== optId));
        optimisticMessagesRef.current.delete(optId);
        messageSeqSetRef.current.delete(optId);

        // Hapus dari mapping jika ada
        const mappedRealSeq = optIdToRealSeqRef.current.get(optId);
        if (mappedRealSeq) {
          optIdToRealSeqRef.current.delete(optId);
          realSeqToOptIdRef.current.delete(mappedRealSeq);
          pendingRealSeqRef.current.delete(mappedRealSeq);
        }

        throw err;
      }
    },
    [tinodeRef],
  );

  const removeOptimisticMessage = useCallback((optId) => {
    setMessages((prev) => prev.filter((m) => m.seq !== optId));
    optimisticMessagesRef.current.delete(optId);
    messageSeqSetRef.current.delete(optId);
  }, []);

  const loadOlderMessages = useCallback(
    async (topicName, beforeSeq) => {
      if (!tinodeRef.current || !topicName) return;

      try {
        const topic = tinodeRef.current.getTopic(topicName);
        if (!topic?.isSubscribed()) return;

        await loadOlderTinodeMessages(
          tinodeRef.current,
          topicName,
          20,
          beforeSeq,
        );

        const newMessages = [];
        const newSeqSet = new Set(); // FIX: Gunakan Set lokal untuk de-duplikasi

        iterateTopicMessages(topic, (m) => {
          if (!m?.seq) return;
          if (isSessionEndMessage(m)) return;
          // FIX: Skip jika seq sudah ada di messageSeqSetRef atau di newSeqSet (prevent duplicate)
          if (messageSeqSetRef.current.has(m.seq) || newSeqSet.has(m.seq))
            return;
          messageSeqSetRef.current.add(m.seq);
          newSeqSet.add(m.seq);
          newMessages.push(formatMessageForDashboard(m, topicName));
        });

        if (newMessages.length > 0) {
          setMessages((prev) => {
            // FIX: Gunakan Map untuk de-duplikasi berdasarkan seq
            const map = new Map();

            // Tambahkan semua pesan existing ke map (de-duplikasi)
            prev.forEach((msg) => {
              if (msg.seq && !map.has(msg.seq)) {
                map.set(msg.seq, msg);
              }
            });

            // Tambahkan pesan baru hanya jika belum ada di map
            newMessages.forEach((msg) => {
              if (msg.seq && !map.has(msg.seq)) {
                map.set(msg.seq, msg);
              }
            });

            return sortMessagesBySeq(Array.from(map.values()));
          });
        }
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            "[useTinodeMessages] loadOlderMessages failed:",
            parseTinodeError(err),
          );
        }
      }
    },
    [tinodeRef],
  );

  // FIX: Handle Tinode reconnect event untuk refresh messages
  useEffect(() => {
    const handleTinodeReconnected = async () => {
      if (!tinodeRef.current || !tinodeRef.current.isAuthenticated()) {
        return;
      }

      if (!selectedTopic) {
        return;
      }

      console.log(
        "[useTinodeMessages] Tinode reconnected, refreshing messages...",
      );

      try {
        // Reload messages untuk selected topic
        await loadTopicMessages(selectedTopic);
      } catch (err) {
        console.warn(
          "[useTinodeMessages] Failed to refresh messages after reconnect:",
          err,
        );
      }
    };

    window.addEventListener("tinode-reconnected", handleTinodeReconnected);

    return () => {
      window.removeEventListener("tinode-reconnected", handleTinodeReconnected);
    };
  }, [selectedTopic, loadTopicMessages]);

  // FIX: Helper function untuk membuat mapping optId → realSeq setelah sendMessage berhasil
  // Digunakan untuk image messages yang menggunakan skipSend: true
  const mapOptimisticToRealSeq = useCallback((optId, realSeq) => {
    if (!optId || !realSeq) return;

    // FIX: Simpan mapping optId → realSeq untuk tracking yang lebih akurat
    optIdToRealSeqRef.current.set(optId, realSeq);
    realSeqToOptIdRef.current.set(realSeq, optId);

    // FIX: Tambahkan ke pendingRealSeqRef untuk tracking
    pendingRealSeqRef.current.add(realSeq);
  }, []);

  return {
    messages,
    setMessages,
    topicInfo,
    messagesEndRef,
    messagesContainerRef,
    messageSeqSetRef,
    optimisticMessagesRef,
    pendingRealSeqRef,
    loadTopicMessages,
    sendOptimisticMessage,
    removeOptimisticMessage,
    loadOlderMessages,
    mapOptimisticToRealSeq,
  };
};
